// import React, { useEffect, useState } from "react";
// import { Paper, TextField, Typography, Button, Box } from "@mui/material";

// const DisplayDoctrines = () => {
//   const API_URL = process.env.REACT_APP_API_URL;

//   const [tree, setTree] = useState([]);
//   const [search, setSearch] = useState("");

//   const fetchTree = async () => {
//     const res = await fetch(
//       `${API_URL}/enterprise/doctrines/tree?search=${search}`,
//     );
//     const result = await res.json();
//     if (result.success) setTree(result.data);
//   };

//   useEffect(() => {
//     fetchTree();
//   }, [search]);

//   const renderTree = (nodes) =>
//     nodes.map((n) => (
//       <Box key={n.id} sx={{ ml: 2, mt: 1 }}>
//         <Paper sx={{ p: 1 }}>
//           <Typography>
//             <b>{n.title}</b>
//           </Typography>
//           <Button size="small">View</Button>
//         </Paper>

//         {n.children?.length > 0 && renderTree(n.children)}
//       </Box>
//     ));

//   return (
//     <div>
//       <TextField
//         fullWidth
//         label="Search Doctrines"
//         value={search}
//         onChange={(e) => setSearch(e.target.value)}
//       />

//       {renderTree(tree)}
//     </div>
//   );
// };

// export default DisplayDoctrines;

import React, { useEffect, useState } from "react";
import {
  Paper,
  TextField,
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  CircularProgress,
  Stack,
  Chip,
} from "@mui/material";

const DisplayDoctrines = () => {
  const API_URL = process.env.REACT_APP_API_URL;

  const [tree, setTree] = useState([]);
  const [search, setSearch] = useState("");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);

  const [selectedDoctrine, setSelectedDoctrine] = useState(null);
  const [openView, setOpenView] = useState(false);

  const [expandedNodes, setExpandedNodes] = useState({});

  const fetchTree = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${API_URL}/enterprise/doctrines/tree?search=${search}`,
      );

      const result = await res.json();

      if (result.success) {
        setTree(result.data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, [search]);

  const handleView = (node) => {
    setSelectedDoctrine(node);
    setOpenView(true);
  };

  const handleEdit = (node) => {
    console.log("Edit:", node);
  };

  const handleDelete = (node) => {
    if (window.confirm(`Delete ${node.title}?`)) {
      console.log("Delete:", node);
    }
  };

  const toggleExpand = (id) => {
    setExpandedNodes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const flattenTree = (nodes) => {
    let result = [];

    nodes.forEach((node) => {
      result.push(node);

      if (node.children?.length) {
        result = [...result, ...flattenTree(node.children)];
      }
    });

    return result;
  };

  const flatData = flattenTree(tree);

  const paginatedData = flatData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const renderTree = (nodes) =>
    nodes.map((node) => (
      <Box key={node.id} sx={{ ml: 2, mt: 1 }}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {node.title}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              ID: {node.id}
            </Typography>

            {node.children?.length > 0 && (
              <Chip
                size="small"
                label={`${node.children.length} Children`}
                sx={{ mt: 1 }}
              />
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {node.children?.length > 0 && (
              <Button variant="outlined" onClick={() => toggleExpand(node.id)}>
                {expandedNodes[node.id] ? "Collapse" : "Expand"}
              </Button>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={() => handleView(node)}
            >
              View
            </Button>

            <Button
              variant="contained"
              color="warning"
              onClick={() => handleEdit(node)}
            >
              Edit
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={() => handleDelete(node)}
            >
              Delete
            </Button>
          </Stack>
        </Paper>

        {expandedNodes[node.id] &&
          node.children?.length > 0 &&
          renderTree(node.children)}
      </Box>
    ));

  return (
    <Box p={2}>
      <Typography variant="h5" gutterBottom>
        Doctrine Management
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField
            fullWidth
            label="Search Doctrines"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <Button variant="contained" onClick={fetchTree}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="body1">
          Total Records: <b>{flatData.length}</b>
        </Typography>
      </Paper>

      {loading ? (
        <Box textAlign="center" mt={5}>
          <CircularProgress />
        </Box>
      ) : (
        renderTree(paginatedData)
      )}

      <TablePagination
        component="div"
        count={flatData.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
      />

      <Dialog
        open={openView}
        onClose={() => setOpenView(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Doctrine Details</DialogTitle>

        <DialogContent dividers>
          {selectedDoctrine && (
            <>
              <Typography>
                <b>ID:</b> {selectedDoctrine.id}
              </Typography>

              <Typography mt={2}>
                <b>Title:</b> {selectedDoctrine.title}
              </Typography>

              <Typography mt={2}>
                <b>Description:</b>
              </Typography>

              <Paper sx={{ p: 2, mt: 1 }}>
                {selectedDoctrine.description || "No Description"}
              </Paper>

              <Typography mt={2}>
                <b>Children:</b> {selectedDoctrine.children?.length || 0}
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenView(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DisplayDoctrines;
